/**
 * Loads the pinned Simple Icons metadata catalog so admins can browse and pick
 * brands to cache into R2. End-user devices never hit this URL.
 */

import { SIMPLE_ICONS_CDN_VERSION, simpleIconsSvgUrl } from "./simpleIconsCurated";

export type SimpleIconsCatalogEntry = {
  title: string;
  slug: string;
  hex: string;
  /** Optional aka / dup aliases from Simple Icons metadata. */
  aliases: string[];
};

const CATALOG_URL = `https://cdn.jsdelivr.net/npm/simple-icons@${SIMPLE_ICONS_CDN_VERSION}/data/simple-icons.json`;

let cachedCatalog: SimpleIconsCatalogEntry[] | null = null;
let inflight: Promise<SimpleIconsCatalogEntry[]> | null = null;

type RawIcon = {
  title?: string;
  slug?: string;
  hex?: string;
  aliases?: {
    aka?: string[];
    dup?: Array<string | { title?: string }>;
  };
};

function normalizeAliases(raw: RawIcon["aliases"]): string[] {
  if (!raw) return [];
  const out: string[] = [];
  for (const aka of raw.aka ?? []) {
    if (typeof aka === "string" && aka.trim()) out.push(aka.trim());
  }
  for (const dup of raw.dup ?? []) {
    if (typeof dup === "string" && dup.trim()) {
      out.push(dup.trim());
      continue;
    }
    if (dup && typeof dup === "object" && typeof dup.title === "string" && dup.title.trim()) {
      out.push(dup.title.trim());
    }
  }
  return out;
}

function normalizeEntry(raw: RawIcon): SimpleIconsCatalogEntry | null {
  const slug = (raw.slug ?? "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  const title = (raw.title ?? "").trim();
  if (!slug || !title) return null;
  const hex = (raw.hex ?? "000000").replace(/^#/, "").toUpperCase();
  return {
    title,
    slug,
    hex: /^[0-9A-F]{6}$/.test(hex) ? hex : "000000",
    aliases: normalizeAliases(raw.aliases),
  };
}

/** Fetch once per session (pinned CDN URL is immutable). */
export async function loadSimpleIconsCatalog(force = false): Promise<SimpleIconsCatalogEntry[]> {
  if (!force && cachedCatalog) return cachedCatalog;
  if (!force && inflight) return inflight;

  inflight = (async () => {
    const res = await fetch(CATALOG_URL);
    if (!res.ok) {
      throw new Error(`Could not load Simple Icons catalog (${res.status}).`);
    }
    const payload = (await res.json()) as unknown;
    if (!Array.isArray(payload)) {
      throw new Error("Simple Icons catalog response was not a list.");
    }
    const entries = payload
      .map((row) => normalizeEntry(row as RawIcon))
      .filter((row): row is SimpleIconsCatalogEntry => row !== null)
      .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));
    cachedCatalog = entries;
    return entries;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export function filterSimpleIconsCatalog(
  entries: SimpleIconsCatalogEntry[],
  query: string,
  limit = 120,
): SimpleIconsCatalogEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return entries.slice(0, limit);
  const scored: Array<{ entry: SimpleIconsCatalogEntry; score: number }> = [];
  for (const entry of entries) {
    const title = entry.title.toLowerCase();
    const slug = entry.slug;
    let score = -1;
    if (slug === q || title === q) score = 0;
    else if (slug.startsWith(q) || title.startsWith(q)) score = 1;
    else if (slug.includes(q) || title.includes(q)) score = 2;
    else if (entry.aliases.some((alias) => alias.toLowerCase().includes(q))) score = 3;
    if (score >= 0) scored.push({ entry, score });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.entry.title.localeCompare(b.entry.title))
    .slice(0, limit)
    .map((row) => row.entry);
}

export function simpleIconsPreviewUrl(slug: string, hex?: string): string {
  // Colored SVG preview for admin only — the published catalog still uses our R2 PNG pipeline.
  const color = (hex ?? "111111").replace(/^#/, "");
  return `https://cdn.simpleicons.org/${encodeURIComponent(slug)}/${color}`;
}

export function catalogEntryToImportDraft(entry: SimpleIconsCatalogEntry): {
  sourceSlug: string;
  displayName: string;
  normalizedName: string;
  matchTerms: string[];
  brandHex: string;
  categoryHint: string;
  sortOrder: number;
} {
  const normalizedName = entry.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  const matchTerms = [
    entry.slug,
    ...entry.aliases.map((alias) => alias.toLowerCase()),
  ].filter((term, index, all) => term && all.indexOf(term) === index && term !== normalizedName);

  return {
    sourceSlug: entry.slug,
    displayName: entry.title,
    normalizedName: normalizedName || entry.slug,
    matchTerms,
    brandHex: entry.hex,
    categoryHint: "Other",
    sortOrder: 500,
  };
}

export { simpleIconsSvgUrl, SIMPLE_ICONS_CDN_VERSION, CATALOG_URL };
